import { MouseEvent, useMemo } from "react";
import { CircularProgress, IconButton, List } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ClearIcon from "@mui/icons-material/Clear";
import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import { IActionPlan } from "entities/RCA/ActionPlanEntity/ActionPlanEntityInterfaces";
import { useActionPlanInlineEditRow } from "./useActionPlanInlineEditRow";
import useRolesPermissions from "hooks/useRolesPermissions";

interface IActionPlanEditionOptionsCellProps {
    row: IActionPlan;
    onDeleteActionPlan?: (actionPlan: IActionPlan) => void;
}

const ActionPlanEditionOptionsCell = ({ row, onDeleteActionPlan }: IActionPlanEditionOptionsCellProps) => {
    const { isEditing, isSaving, isNameValid, startEdit, saveEdit, cancelEdit } = useActionPlanInlineEditRow(row);

    const rcaOwnershipIds = useMemo(
        () =>
            row
                ? [
                      ...(row?.submitted_by?.user_id ? [row.submitted_by.user_id] : []),
                      ...(row?.assignee?.user_id ? [row?.assignee.user_id] : []),
                  ]
                : [],
        [row],
    );
    const { RCA_PERMISSIONS } = useRolesPermissions(rcaOwnershipIds);

    return (
        <List sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {isSaving ? (
                <CircularProgress size={20} color="info" />
            ) : isEditing ? (
                <>
                    <IconButton
                        size="small"
                        disabled={!isNameValid || !RCA_PERMISSIONS?.action_plans.canEdit}
                        onClick={(event: MouseEvent<HTMLButtonElement>) => {
                            event.stopPropagation();
                            saveEdit();
                        }}
                    >
                        <CheckIcon />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={(event: MouseEvent<HTMLButtonElement>) => {
                            event.stopPropagation();
                            cancelEdit();
                        }}
                    >
                        <ClearIcon />
                    </IconButton>
                </>
            ) : (
                <>
                    <IconButton
                        size="small"
                        disabled={!RCA_PERMISSIONS?.action_plans.canEdit}
                        onClick={(event: MouseEvent<HTMLButtonElement>) => {
                            event.stopPropagation();
                            startEdit();
                        }}
                    >
                        <EditIcon />
                    </IconButton>
                    <IconButton
                        size="small"
                        disabled={!RCA_PERMISSIONS?.action_plans.canDelete}
                        onClick={(event: MouseEvent<HTMLButtonElement>) => {
                            event.stopPropagation();
                            if (onDeleteActionPlan) {
                                onDeleteActionPlan(row);
                            }
                        }}
                    >
                        <DeleteIcon />
                    </IconButton>
                </>
            )}
        </List>
    );
};

export default ActionPlanEditionOptionsCell;
